import useSWRImmutable from 'swr/immutable';
import { getInfographUri } from 'utils/getInfographUri';
import { IconWidgetData } from 'widgets/IconWidget/IconWidget.types';

const BASE_ICON_V1_URL = 'https://s3.amazonaws.com/media.venngage.com/icons/v1/';
const BASE_ICONS8_URL = '/infographics/icons8_download/';
const BASE_ICONS8_ILLUSTRATION_URL = '/infographics/icons8_illustration_download/';

const s3IconFetcher = (iconId: string) => fetch(`${BASE_ICON_V1_URL}${iconId}.json?`).then((res) => res.json());
const icons8Fetcher = (iconPath: string) =>
  fetch(getInfographUri(`${BASE_ICONS8_URL}${iconPath}`)).then((res) => res.json());
const icons8IllustrationFetcher = (iconId: string) =>
  fetch(getInfographUri(`${BASE_ICONS8_ILLUSTRATION_URL}${iconId}`)).then((res) => res.text());

export interface IconApiResponse {
  svg: string;
  color: number;
  viewBox?: string;
  description?: string;
  premium?: boolean;
  author?: string;
  old_icon?: number;
}

export const useIcon = (iconId: string): { data: IconApiResponse | undefined; error: Error } => {
  const { data, error } = useSWRImmutable<IconApiResponse>(iconId, s3IconFetcher);

  return { data, error };
};

export type IconRenderData = {
  svg: string;
  isMono: boolean;
  viewBox?: string;
  description?: string;
  permission?: 'default' | 'premium' | 'business';
  author?: string;
  isOldIcon?: boolean;
  iconPath?: string;
  id?: string;
};

interface UseIconDataProps {
  iconId: string;
  iconMetadata?: IconWidgetData['iconMetadata'];
}

const DEFAULT_ICON_DATA: IconRenderData = {
  svg: '',
  isMono: false,
  viewBox: '',
  description: '',
  permission: 'default',
  author: '',
  isOldIcon: false,
} as IconRenderData;

/**
 * Hook to fetch icon data from S3 or icons8 depending on icon widget data
 * Normalizes the data from S3 and icons8 to be the same
 *
 * Returns null if icon data is not available
 *
 * @param iconWidgetData
 * @returns
 */
export const useIconData = (iconWidgetData: UseIconDataProps) => {
  const { iconId, iconMetadata } = iconWidgetData;

  // Variables to determine if the icon is from icons8
  const iconPath = iconMetadata?.iconPath;
  const isIcons8Illustration = !!iconMetadata?.isIllustration;
  const isIcons8 = !!iconPath;
  const isS3Icon = !isIcons8 && !isIcons8Illustration;

  // Fetch icon from S3 if not icons8 icon
  const { data: iconApiData, error: iconApiError } = useSWRImmutable<IconApiResponse>(
    iconId,
    isS3Icon ? s3IconFetcher : null,
  );

  /// Icons8 fetchers - Note: If service is down, skip fetch
  // Fetch icon from icons8 if icons8 icon
  const { data: icons8Data, error: icons8Error } = useSWRImmutable<{ icon: { svg: string } }>(
    isIcons8 ? iconId : null,
    icons8Fetcher,
  );

  // Fetch illustration if icons8 vector
  const { data: icons8IllustrationData, error: icons8IllustrationError } = useSWRImmutable<string>(
    isIcons8Illustration ? iconId : null,
    icons8IllustrationFetcher,
  );

  let iconData: IconRenderData | null = null;

  // Select which data to use based on the type of icon
  // Case 1: Icons8 illustration
  if (isIcons8Illustration) {
    if (icons8IllustrationData && !icons8IllustrationError) {
      iconData = {
        ...DEFAULT_ICON_DATA,
        svg: icons8IllustrationData,
        isMono: false,
        viewBox: iconWidgetData.iconMetadata?.viewBox || '',
      };
    }
  } else if (isIcons8) {
    // Case 2: Normal icons8 icon
    if (icons8Data && !icons8Error && icons8Data.icon.svg) {
      iconData = {
        ...DEFAULT_ICON_DATA,
        svg: icons8Data.icon.svg,
        isMono: !!iconWidgetData.iconMetadata?.isMono,
        viewBox: iconWidgetData.iconMetadata?.viewBox || '',
      };
    }
  } else if (isS3Icon && iconApiData && !iconApiError) {
    // Case 3: S3 icon pulled from algolia search
    const { svg, color, viewBox, description, premium, author, old_icon } = iconApiData;

    iconData = {
      svg,
      isMono: color === 0,
      viewBox: viewBox || '',
      description: description || '',
      permission: premium ? 'premium' : 'default',
      author: author || '',
      isOldIcon: old_icon === 1,
    };
  }
  return iconData;
};
